import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Input from '../../components/Input';
import { useFormik } from 'formik';
import useApi from '../../hooks/use-api';
import { AppRoutes } from '../../utils/props';
import { OutlinedCheck } from '../../icons';

export default function ForgotPasswordPage() {
  const { t } = useTranslation('ns');
  const [submitted, setSubmitted] = useState(false);

  const forgotPassword = useApi({
    path: '/password_token',
    defaultSuccessMessage: t('forgotPasswordEmailSent', { defaultValue: 'If this email exists, a reset link has been sent.' }),
    callback: () => setSubmitted(true),
  });

  const form = useFormik({
    initialValues: { email: '' },
    validate: values => {
      const errors: { email?: string } = {};
      if (!values.email) {
        errors.email = t('emailRequired', { defaultValue: 'Email is required' });
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
        errors.email = t('invalidEmail', { defaultValue: 'Invalid email address' });
      }
      return errors;
    },
    onSubmit: async (values) => {
      await forgotPassword.post({ mail: values.email });
    },
  });

  const { errors, touched, getFieldProps, handleSubmit } = form;

  return (
    <div
      className="relative flex min-h-screen w-full flex-col items-center justify-center bg-cover bg-center"
      style={{
        background: `linear-gradient(180deg, #086CE1 0%, rgba(8, 108, 225, 0) 98%), linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000 98%), url('/images/account-background.jpg') lightgray -2.651px -381.993px/100.239% 142.326% no-repeat`,
      }}
    >
      <div className="relative z-10 flex w-full flex-col items-center">
        <h1 className="mx-auto mb-12 text-white max-w-lg text-center text-3xl font-extrabold text-primary drop-shadow-lg md:text-4xl">
          {t('forgotPasswordTitle', { defaultValue: 'Forgot your password?' })}
        </h1>
        <div className="w-full max-w-md px-4">
          {submitted && (
            <div
              role="status"
              className="mb-4 flex items-start gap-2 rounded-lg border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-700"
            >
              <OutlinedCheck
                width={20}
                height={20}
                hardcodedColor="#15803D"
                className="mt-0.5 shrink-0"
              />
              <span>{t('forgotPasswordEmailSent', { defaultValue: 'If this email exists, a reset link has been sent.' })}</span>
            </div>
          )}
          <form className="flex flex-col items-center px-8 pb-8 pt-10" onSubmit={handleSubmit}>
            <div className="mb-2 w-full">
              <Input
                {...getFieldProps('email')}
                title={t('email')}
                type="email"
                error={touched.email ? (errors.email ?? '') : ''}
                labelClassName="text-white"
              />
            </div>
            <div className="mb-4 flex w-full justify-end">
              <Link
                className="bg-transparent text-xs font-medium text-blue-100 transition hover:underline"
                to={AppRoutes.Login}
              >
                {t('backToLogin', { defaultValue: 'Back to login' })}
              </Link>
            </div>
            <button
              className="mb-6 w-full rounded bg-blue-600 py-3 text-lg font-bold text-white shadow-md transition hover:bg-blue-700"
              type="submit"
              disabled={submitted}
            >
              {t('sendResetLink', { defaultValue: 'Send Reset Link' })}
            </button>
            
          </form>
        </div>
      </div>
    </div>
  );
}